import React, { useState, useCallback, useRef, useEffect } from 'react'

import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useParams, useHistory } from 'react-router-dom'

import camera from '@console/common/assets/camera.svg'
import refresh from '@console/common/assets/refresh.svg'
import Button from '@console/common/components/Button'
import Cascade, { SelectOptions } from '@console/common/components/Cascade'
import TextInput, { InputTypes } from '@console/common/components/TextInput'
import { Customer, Language } from '@console/common/services/api'
import { upload } from '@console/common/services/upload'

import Header from '../../../components/Header'
import { getCustomer, patchCustomer } from '../../../services/api'
import {
  Container,
  DropzoneContainer,
  RefreshLogo,
  FieldNames,
  CascadeRow,
  ButtonContainer
} from './styles'

interface ParamTypes {
  customerId: string
}

const Edit: React.FC = () => {
  const { t } = useTranslation(['settings'])
  const { customerId } = useParams<ParamTypes>()
  const history = useHistory()
  const [loading, setLoading] = useState<boolean>(false)
  const [logo, setLogo] = useState<string | null>(null)
  const [name, setName] = useState<string>('')
  const [slug, setSlug] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [analytics, setAnalytics] = useState<string>('')
  const [customerMetadata, setCustomerMetadata] = useState<Customer['metadata']>({})
  const [language, setLanguage] = useState<SelectOptions | null>(null)
  const [waterMark, setWaterMark] = useState<SelectOptions | null>(null)
  const [status, setStatus] = useState<SelectOptions | null>(null)
  const [validity, setValidity] = useState<boolean>(false)
  const nameRef = useRef<HTMLDivElement>(null)
  const emailRef = useRef<HTMLDivElement>(null)
  const analyticsRef = useRef<HTMLDivElement>(null)

  const LanguageOptions = [
    {
      value: Language.EN,
      label: Language.EN
    },
    {
      value: Language.PT,
      label: Language.PT
    }
  ]

  const WatermarkOptions = [
    {
      value: 'bottomLeft',
      label: t('customers.creation.bottomLeft')
    },
    {
      value: 'bottomRight',
      label: t('customers.creation.bottomRight')
    },
    {
      value: 'topLeft',
      label: t('customers.creation.topLeft')
    },
    {
      value: 'topRight',
      label: t('customers.creation.topRight')
    },
    {
      value: '',
      label: t('customers.creation.none')
    }
  ]

  const StatusOptions = [
    {
      value: true,
      label: t('customers.active')
    },
    {
      value: false,
      label: t('customers.inactive')
    }
  ]

  useEffect(() => {
    setLoading(true)
    getCustomer(customerId).then(async (iCustomer) => {
      if (iCustomer) {
        setLogo(iCustomer.logoUrl)
        setName(iCustomer.name)
        setSlug(iCustomer.slug)
        setEmail(iCustomer.metadata?.email || '')
        setAnalytics(iCustomer.metadata?.performance?.['ga4-analytics'] || '')
        setCustomerMetadata(iCustomer.metadata || {})
        setLanguage(LanguageOptions.filter((l) => l.value === iCustomer.language)[0])
        setWaterMark(WatermarkOptions.filter((l) => l.value === iCustomer.waterMarkPosition)[0])
        setStatus(StatusOptions.filter((l) => l.value === iCustomer.active)[0])
        setLoading(false)
      }
    })
  }, [])

  useEffect(() => {
    setSlug(
      name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase()
    )
  }, [name])

  useEffect(() => {
    const nameValidity = (nameRef.current?.children[0] as HTMLInputElement)?.checkValidity()
    const emailValidity = (emailRef.current?.children[0] as HTMLInputElement)?.checkValidity()
    const analyticsValidity = (
      analyticsRef.current?.children[0] as HTMLInputElement
    )?.checkValidity()
    setValidity(
      nameValidity && emailValidity && analyticsValidity && !!slug && language?.value && status
    )
  }, [name, slug, email, analytics, language, waterMark, status])

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file: File) => {
      upload(file, 'logo', setLogo)
    })
  }, [])

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: 'image/*',
    noClick: true,
    noKeyboard: true,
    noDragEventsBubbling: true,
    maxFiles: 1
  })

  const triggerUpdate = async () => {
    if (!validity) return
    await patchCustomer(customerId, {
      name,
      slug,
      metadata: { ...customerMetadata, email, performance: { 'ga4-analytics': analytics } },
      language: language?.value,
      waterMarkPosition: waterMark?.value,
      active: status?.value,
      logoUrl: logo || undefined
    })
    history.push('/settings/customers')
  }

  return (
    <Container>
      <Header returnPage='/settings/customers' />
      <DropzoneContainer {...getRootProps()}>
        <input {...getInputProps()} />
        {logo ? (
          <>
            <img src={logo} alt='logo' />
            <RefreshLogo
              src={refresh}
              onClick={open}
              onKeyPress={open}
              alt=''
              role='presentation'
            />
          </>
        ) : (
          <div role='button' onClick={open} onKeyPress={open} tabIndex={0}>
            <img src={camera} alt='upload' />
          </div>
        )}
      </DropzoneContainer>
      <FieldNames isLoading={loading}>{t('customers.creation.name')}</FieldNames>
      <div ref={nameRef}>
        <TextInput
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder={t('customers.creation.nameExample')}
          required
          width='487px'
          isLoading={loading}
        />
      </div>
      <FieldNames isLoading={loading}>{t('customers.creation.slug')}</FieldNames>
      <TextInput
        value={slug}
        onChange={(e) => setSlug(e.currentTarget.value)}
        required
        disabled
        width='487px'
        isLoading={loading}
      />
      <FieldNames isLoading={loading}>{t('customers.creation.email')}</FieldNames>
      <div ref={emailRef}>
        <TextInput
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          placeholder={t('customers.creation.emailExample')}
          required
          type={InputTypes.EMAIL}
          width='487px'
          isLoading={loading}
        />
      </div>
      <FieldNames isLoading={loading}>{t('customers.creation.analytics')}</FieldNames>
      <div ref={analyticsRef}>
        <TextInput
          value={analytics}
          onChange={(e) => setAnalytics(e.currentTarget.value)}
          placeholder={t('customers.creation.analyticsExample')}
          type={InputTypes.URL}
          width='487px'
          isLoading={loading}
        />
      </div>
      <CascadeRow>
        <div>
          <FieldNames isLoading={loading}>{t('customers.creation.language')}</FieldNames>
          <Cascade
            value={language}
            isSearchable={false}
            options={LanguageOptions}
            onChange={(option) => setLanguage(option)}
            isLoading={loading}
          />
        </div>
        <div>
          <FieldNames isLoading={loading}>{t('customers.creation.watermarkPosition')}</FieldNames>
          <Cascade
            value={waterMark}
            isSearchable={false}
            options={WatermarkOptions}
            onChange={(option) => setWaterMark(option)}
            isLoading={loading}
          />
        </div>
      </CascadeRow>
      <FieldNames isLoading={loading}>{t('customers.creation.status')}</FieldNames>
      <Cascade
        value={status}
        isSearchable={false}
        options={StatusOptions}
        onChange={(option) => setStatus(option)}
        isLoading={loading}
      />
      <ButtonContainer>
        <Button onClick={triggerUpdate} disabled={!validity} isLoading={loading}>
          {t('customers.creation.saveEdit')}
        </Button>
      </ButtonContainer>
    </Container>
  )
}

export default Edit
